// Builder Evaluation Service - Risk prioritization for Builder assessments
// Uses Builder-specific Redux state (builderRisks, not explorerRisks or modelRisks)
import {
  updateBuilderRiskPriority
} from '../../redux/slices/risksSlice';
import { authFetch } from '../../utils/authFetch';
import {
  setExplorerRiskEvaluation,
  saveExplorerEvaluation
} from '../../redux/slices/riskEvaluationsSlice';

export class BuilderEvaluationService {
  constructor(dispatch, modelId, assessmentUuid, contextData = {}, impactCategories = [], liveUpdateCallback = null) {
    this.dispatch = dispatch;
    this.modelId = modelId;
    this.assessmentUuid = assessmentUuid;
    this.contextData = contextData;
    this.impactCategories = impactCategories;
    this.liveUpdateCallback = liveUpdateCallback;
  }

  async executeEvaluation(areaKey, areaDefinition, riskFormTaxonomy, inheritedRisks, progressCallback) {
    try {
      if (progressCallback) progressCallback('evaluation');
      await new Promise(resolve => setTimeout(resolve, 600));

      // Get fresh Redux state
      const getState = () => this.dispatch((dispatch, getState) => getState());
      const currentState = getState();

      // Access Builder risks (not explorerRisks or modelRisks)
      const builderRisks = currentState.riskItems?.byAssessment?.[this.assessmentUuid]?.builderRisks || {};
      const areaRisks = builderRisks[areaKey]?.areaRisks || {};

      console.log(`[BuilderEvaluationService] State source for area: ${areaKey}`);
      console.log(`[BuilderEvaluationService] Assessment UUID:`, this.assessmentUuid);
      console.log(`[BuilderEvaluationService] Available risk categories:`, Object.keys(areaRisks));

      // Find risks that need evaluation
      const risksToEvaluate = [];

      Object.entries(areaRisks).forEach(([category, riskArray]) => {
        console.log(`[BuilderEvaluationService] Checking category "${category}": ${Array.isArray(riskArray) ? riskArray.length : 'not array'} risks`);
        if (Array.isArray(riskArray)) {
          riskArray.forEach((risk, idx) => {
            console.log(`[BuilderEvaluationService] Risk ${idx}: "${risk.riskName}" - status: ${risk.status}, priority: ${risk.priority}`);
            if (risk.status === 'Saved' && !risk.priority) {
              console.log(`[BuilderEvaluationService] FOUND unevaluated risk: "${risk.riskName}"`);
              risksToEvaluate.push({
                ...risk,
                areaKey,
                category
              });
            }
          });
        }
      });

      if (risksToEvaluate.length > 0) {
        console.log(`[BuilderEvaluationService] Evaluating ${risksToEvaluate.length} risks using Builder FastTrack approach`);

        let evaluatedCount = 0;
        for (const risk of risksToEvaluate) {
          try {
            const sourceCauses = this.extractCausesForRisk(risk, areaDefinition);

            console.log(`[BuilderEvaluationService] Causes for ${risk.riskName}:`, {
              causesCount: sourceCauses?.length || 0
            });

            // Call Builder-specific FastTrack evaluation
            const evalResponse = await authFetch('/.netlify/functions/builderPrioritizeRisks', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                riskName: risk.riskName,
                areaName: areaDefinition?.label || areaKey,
                evaluationData: {
                  causes: sourceCauses,
                  category: risk.category,
                  rationale: risk.rationale,
                  confidence: risk.confidence
                },
                assessmentUuid: this.assessmentUuid,
                parentAssessmentUuid: this.contextData?.parentAssessmentUuid,
                businessContext: this.contextData?.businessContext,
                stakeholders: this.contextData?.stakeholders || []
              })
            });

            if (evalResponse.ok) {
              const evalData = await evalResponse.json();
              if (evalData.fastTrackEvaluation) {
                const evaluation = evalData.fastTrackEvaluation;
                const priority = evaluation.priority?.toLowerCase() || 'moderate';
                const rationale = evaluation.rationale || '';

                console.log(`[BuilderEvaluationService] FastTrack evaluated ${risk.riskName}: ${priority}`);

                await this.saveBuilderEvaluation(risk, evaluation, priority, rationale, areaKey);

                evaluatedCount++;

                if (this.liveUpdateCallback) {
                  this.liveUpdateCallback('riskEvaluation', {
                    riskName: risk.riskName,
                    priority: priority,
                    rationale: rationale,
                    confidence: evaluation.confidence,
                    themes: evaluation.fastTrackData?.selectedThemes?.length || 0,
                    evaluatedCount,
                    totalRisks: risksToEvaluate.length,
                    areaKey
                  });
                }
              }
            } else {
              console.error(`[BuilderEvaluationService] FastTrack evaluation failed for ${risk.riskName}:`, await evalResponse.text());
            }
          } catch (evalError) {
            console.error(`[BuilderEvaluationService] Risk evaluation failed for ${risk.riskName}:`, evalError);
          }
        }
      }

      return {
        evaluatedRisks: risksToEvaluate.length,
        areaKey
      };

    } catch (error) {
      console.error('[BuilderEvaluationService] Evaluation failed:', error);
      throw error;
    }
  }

  extractCausesForRisk(risk, areaDefinition) {
    try {
      const fromRiskSources = areaDefinition?.riskSources?.find(src => src.name === risk.category)?.causes;
      if (fromRiskSources && Object.keys(fromRiskSources).length > 0) {
        console.log(`[BuilderEvaluationService] Found causes from riskSources for: ${risk.category}`);
        return Object.entries(fromRiskSources).map(([name, data]) => ({
          name,
          description: data.description || data.rationale || '',
          ...data
        }));
      }

      console.log(`[BuilderEvaluationService] No causes found for risk category: ${risk.category}`);
      return [];
    } catch (error) {
      console.error('[BuilderEvaluationService] Error extracting causes:', error);
      return [];
    }
  }

  async saveBuilderEvaluation(risk, evaluation, priority, rationale, areaKey) {
    try {
      // STEP 1: Save evaluations to DB using RTK thunk (reuse Explorer evaluation infrastructure)
      const themes = evaluation.fastTrackData?.selectedThemes || [];

      if (themes.length > 0) {
        const savePromises = themes.map(theme =>
          this.dispatch(saveExplorerEvaluation({
            modelUuid: this.modelId,
            assessmentUuid: this.assessmentUuid,
            riskName: risk.riskName,
            riskId: risk.riskId,
            themeId: theme.themeId,
            riskArea: areaKey,
            priority: priority,
            rationale: rationale,
            data: theme.evaluation?.impact || theme.highLevelAssessment || {}
          })).unwrap().catch(err => {
            console.error(`[BuilderEvaluationService] Failed to save theme ${theme.themeId} for ${risk.riskName}:`, err);
            return { error: true };
          })
        );

        const results = await Promise.all(savePromises);
        const failedSaves = results.filter(r => r.error);

        if (failedSaves.length > 0) {
          console.error(`[BuilderEvaluationService] Failed to save ${failedSaves.length}/${themes.length} theme evaluations for ${risk.riskName}`);
        } else {
          console.log(`[BuilderEvaluationService] Successfully saved ${themes.length} theme evaluations for ${risk.riskName}`);
        }
      } else {
        console.log(`[BuilderEvaluationService] No themes for ${risk.riskName}, saving with default theme`);
        try {
          await this.dispatch(saveExplorerEvaluation({
            modelUuid: this.modelId,
            assessmentUuid: this.assessmentUuid,
            riskName: risk.riskName,
            riskId: risk.riskId,
            themeId: 1,
            riskArea: areaKey,
            priority: priority,
            rationale: rationale,
            data: {}
          })).unwrap();
          console.log(`[BuilderEvaluationService] Default evaluation saved for ${risk.riskName}`);
        } catch (saveError) {
          console.error(`[BuilderEvaluationService] Failed to save default evaluation for ${risk.riskName}:`, saveError);
        }
      }

      // STEP 2: Update Builder Redux state with priority
      this.dispatch(
        updateBuilderRiskPriority({
          assessmentUuid: this.assessmentUuid,
          areaKey,
          category: risk.category,
          riskId: risk.riskId,
          priority,
          rationale
        })
      );

      // STEP 3: Store evaluation in evaluations slice (reuse Explorer evaluation slice)
      this.dispatch(
        setExplorerRiskEvaluation({
          assessmentUuid: this.assessmentUuid,
          areaKey,
          category: risk.category,
          riskId: risk.riskId,
          evaluation: {
            priority,
            rationale,
            confidence: evaluation.confidence,
            fastTrackData: evaluation.fastTrackData,
            evaluatedAt: new Date().toISOString()
          }
        })
      );

      console.log(`[BuilderEvaluationService] Priority saved for ${risk.riskName}: ${priority}`);

    } catch (error) {
      console.error(`[BuilderEvaluationService] Error saving Builder evaluation for ${risk.riskName}:`, error);
    }
  }
}
